import {useCallback, useEffect, useRef, useState} from 'react';
import type {ChangeEvent} from 'react';
import {useSelector} from 'react-redux';
import * as reduxSelectors from '../store/application.reducers';
import * as Api from '../../utils/api/api';
import {USE_MOCK_MANAGERS, getMockNearestManagers} from './managers.mockData';
import {userHasAccessRights} from 'utils/accessRights.function.js';
import {handleError, insufficientRightsError} from 'utils/errorHandle.function.js';
import {canViewConfiguration} from '../menu/Menu.component';
import {
    MANAGER_BY_EMPLOYEE,
    NearestManagerMode,
    NearestManagerRow,
    NearestManagersResponse
} from 'types/managers';

export const FIRST_PAGE = 1;
export const ITEMS_PER_PAGE = 20;
// the endpoint takes a sort direction, but the page offers no way to flip it
const SORT_ASCENDING = true;

export interface NearestManagers {
    rows: NearestManagerRow[];
    /** number of matching rows across every page, which is what the paging stops at */
    records: number;
    isLoading: boolean;
    searchValue: string;
    nearestManagerMode: NearestManagerMode;
    /** the mode the page opens in: the employee is named first, the manager second */
    isManagerByEmployee: boolean;
    /** the list memoises its rows, and needs the language to know when a translation went stale */
    currentLanguage: string;
    inputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    clearSearch: () => void;
    onModeChange: (mode: NearestManagerMode) => void;
    loadMoreItems: () => void;
}

/**
 * The Managers page's data: the paged NearestManagers request, the search term and the mode
 * that drives it, plus the rights check the route does not do.
 *
 * The ag-grid rendering (behind fe-use-ng-grid-in-configuration) reads the page state from this hook.
 * Classic managers list is not aligned with this.
 **/
export function useNearestManagers(): NearestManagers {

    const userData = useSelector(reduxSelectors.getUsersData);
    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const currentLanguage = useSelector(reduxSelectors.getLanguage);

    const [searchValue, setSearchValue] = useState('');
    const [nearestManagerMode, setNearestManagerMode] = useState<NearestManagerMode>(MANAGER_BY_EMPLOYEE);
    /**
     * Where the paging has got to: the last page that landed. It is only ever carried forward by
     * a response - a new query sends it back to the first page - so a request that fails, or is
     * overtaken by a newer one, leaves it counting from the rows actually on screen. The next
     * bottom hit then asks for that same page again instead of stepping over it.
     */
    const [page, setPage] = useState(FIRST_PAGE);
    const [rows, setRows] = useState<NearestManagerRow[]>([]);
    const [records, setRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Whether a request is on its way, which is what holds the paging back.
     *
     * It has to be a ref rather than the isLoading state: two bottom hits can land in the same
     * tick, before React has re-rendered with the state the first one set.
     */
    const requestInFlight = useRef(false);

    // every call gets a ticket, and only the newest one is allowed to write to the state
    const latestRequest = useRef(0);

    /**
     * The query is passed in rather than read off the state, so that a handler can load the page it
     * is switching to without having to wait for its own state update to land.
     */
    const fetchManagers = useCallback((requestedPage: number, mode: NearestManagerMode, search: string) => {
        const requestId = ++latestRequest.current;
        requestInFlight.current = true;
        setIsLoading(true);

        const request = USE_MOCK_MANAGERS
            ? getMockNearestManagers(requestedPage, ITEMS_PER_PAGE, mode, SORT_ASCENDING, search)
            : Api.getNearestManagers(requestedPage, ITEMS_PER_PAGE, mode, SORT_ASCENDING, search, currentContextId);

        request.then((response: NearestManagersResponse) => {
                if (requestId !== latestRequest.current) {
                    // a newer request has already been fired, so this response is stale - and
                    // that newer one is still on its way, so the paging stays held back
                    return;
                }
                requestInFlight.current = false;
                setPage(response.page);
                setRecords(response.records);
                // the first page replaces what is on screen, every page after it extends the list
                setRows(previous => requestedPage === FIRST_PAGE ? response.rows : [...previous, ...response.rows]);
                setIsLoading(false);
            },
            (error: any) => {
                if (requestId !== latestRequest.current) {
                    return;
                }
                requestInFlight.current = false;
                setIsLoading(false);
                console.error(error);
            });
    }, [currentContextId]);

    useEffect(() => {
        // with no location to go on, the rights check reads the path the browser is already on
        const userRoles = userData?.userRoles;
        if (!userRoles) {
            return;
        }
        if (!userHasAccessRights(userRoles) || !canViewConfiguration(userRoles)) {
            handleError(insufficientRightsError);
        }
    }, [userData]);
    // loads on mount, and starts over whenever the company context changes
    useEffect(() => {
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, searchValue);
    }, [currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- the mode and the search term reload through their own handlers

    const inputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, value);
    }, [fetchManagers, nearestManagerMode]);

    const clearSearch = useCallback(() => {
        setSearchValue('');
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, nearestManagerMode, '');
    }, [fetchManagers, nearestManagerMode]);

    const onModeChange = useCallback((mode: NearestManagerMode) => {
        if (mode === nearestManagerMode) {
            return;
        }
        setNearestManagerMode(mode);
        setPage(FIRST_PAGE);
        fetchManagers(FIRST_PAGE, mode, searchValue);
    }, [fetchManagers, nearestManagerMode, searchValue]);

    /**
     * The page after the one on screen, if the list has one and nothing is already being loaded.
     *
     * Two things keep the paging in step, and both matter: nothing is asked for while a request
     * is in flight, and the page counter is never moved on ahead of a response. Were either
     * missing, the next page asked for would be the one after a page that had not landed - and
     * that page would then be missing from the middle of the list for good, since `records` is
     * never reached and the rows are only ever appended.
     */
    const loadMoreItems = useCallback(() => {
        if (requestInFlight.current) {
            return;
        }
        if (rows.length > 0 && rows.length < records) {
            fetchManagers(page + 1, nearestManagerMode, searchValue);
        }
    }, [fetchManagers, nearestManagerMode, page, records, rows.length, searchValue]);

    return {
        rows,
        records,
        isLoading,
        searchValue,
        nearestManagerMode,
        isManagerByEmployee: nearestManagerMode === MANAGER_BY_EMPLOYEE,
        currentLanguage,
        inputChange,
        clearSearch,
        onModeChange,
        loadMoreItems
    };
}
